<template>
  <nav class="navbar navbar-expand-lg">
    <div class="container-fluid">
      <!-- Mobile sidebar toggle button. d-xl-none: visible below 1200px,
           matching the breakpoint where the sidebar goes off-canvas
           (d-lg-none left a 992-1199px window with a hidden sidebar and no
           way to open it) -->
      <button
        class="navbar-toggler d-xl-none"
        type="button"
        @click="toggleSidebar"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon">
          <span class="icon-bar"></span>
          <span class="icon-bar"></span>
          <span class="icon-bar"></span>
        </span>
      </button>

      <div class="navbar-brand d-flex align-items-center">
        <span class="user-info text-white">
          <span class="user-name">{{
            (store?.user?.first_name + " " + store?.user?.last_name).trim() ||
            "User"
          }}</span>
          <span class="user-type badge badge-secondary ml-2">{{
            formattedUserType
          }}</span>
        </span>

        <!-- Impersonation Dropdown (Super Admin Only) -->
        <AIVSelect
          v-if="store.isSuperAdmin && !store.isImpersonating"
          v-model="selectedUserId"
          :options="formattedImpersonatableUsers"
          placeholder="Impersonate User"
          :searchable="true"
          :clearable="true"
          class="ml-3 impersonation-select"
          @change="handleImpersonate"
          :custom-style="{ width: '250px', height: '32px', fontSize: '14px' }"
        />

        <!-- Stop Impersonating Button -->
        <el-button
          v-if="store.isImpersonating"
          type="warning"
          size="small"
          class="ml-3"
          @click="stopImpersonating"
        >
          <i class="fa-solid fa-user-slash mr-1"></i>
          Stop Impersonating
        </el-button>

        <!-- Clock In Button (hidden for group shifts once clocked in) -->
        <el-button
          v-if="currentShift && showClockInButton"
          type="primary"
          size="small"
          class="ml-3 clock-btn clock-in-btn"
          @click="handleClockAction"
        >
          <i class="fa-solid fa-clock mr-1"></i>
          Clock In
        </el-button>

        <!-- Tasks Button (DSPD group shifts only) -->
        <el-button
          v-if="showNavTasksButton"
          type="primary"
          size="small"
          class="ml-3 clock-btn"
          @click="openGroupTasks"
        >
          <i class="fa-solid fa-list-check mr-1"></i>
          Tasks
        </el-button>

        <!-- Offline / pending sync status chip -->
        <OfflineSyncStatus class="ml-2" />

        <!-- Clock Out Button (for group shifts, only when all tasks complete) -->
        <el-button
          v-if="currentShift && showClockOutButton"
          type="primary"
          size="small"
          class="ml-2 clock-btn clock-out-btn"
          @click="handleClockAction"
        >
          <i class="fa-solid fa-clock mr-1"></i>
          Clock Out
        </el-button>

        <!-- Medications Button -->
        <el-button
          v-if="showMedicationsButton"
          type="info"
          size="small"
          class="ml-2 meds-btn"
          @click="showMedicationModal = true"
        >
          <i class="fa-solid fa-pills mr-1"></i>
          Medications
        </el-button>

        <!-- Start/End Break Button -->
        <el-button
          v-if="shouldShowBreakButton"
          type="warning"
          size="small"
          class="ml-2 break-btn"
          @click="handleStartBreak"
        >
          <i :class="currentShift.isOnBreak ? 'fa-solid fa-play mr-1' : 'fa-solid fa-pause mr-1'"></i>
          {{
            currentShift.isOnBreak
              ? "End Break"
              : "Start Break"
          }}
        </el-button>

        <!-- Report Incident Button (always visible) -->
        <el-button
          type="danger"
          size="small"
          class="ml-2 incident-btn"
          @click="openIncidentModal"
        >
          <i class="fa-solid fa-exclamation-triangle mr-1"></i>
          Incident
        </el-button>
      </div>

      <!-- Logout link moved to main navbar area -->
      <div class="navbar-actions d-flex align-items-center">
        <a
          v-if="!store.isAdmin"
          href="#"
          class="nav-link d-flex align-items-center p-0 mr-3 profile-link"
          @click.prevent="goToMyProfile"
        >
          <i class="fa fa-user mr-2"></i>
          <span class="profile-text">My Profile</span>
        </a>
        <a
          href="#"
          class="nav-link d-flex align-items-center p-0"
          @click.prevent="logout"
        >
          <i class="fa fa-sign-out-alt mr-2"></i>
          <span class="logout-text">Log Out</span>
        </a>
      </div>
    </div>

    <!-- Clock In Modal -->
    <ClockInModal
      :visible.sync="showClockInModal"
      :shift="currentShift"
      :user-id="store?.user?.id"
      @clock-in-success="onClockInSuccess"
    />

    <!-- Clock Out Modal -->
    <ClockOutModal
      :visible.sync="showClockOutModal"
      :shift="currentShift"
      :user-id="store?.user?.id"
      :pre-administered-medications="midShiftMedications"
      @clock-out-success="onClockOutSuccess"
    />

    <!-- Medication Admin Modal -->
    <MedicationAdminModal
      :visible.sync="showMedicationModal"
      :shift="currentShift"
      :user-id="store?.user?.id"
      :previous-administrations="midShiftMedications"
      @medication-save="onMedicationSave"
    />

    <!-- Start Break Modal -->
    <StartBreakModal
      :visible.sync="showStartBreakModal"
      :shift="currentShift"
      :user-id="store?.user?.id"
      @break-action-success="onBreakActionSuccess"
    />

    <!-- Group Shift Tasks Modal -->
    <GroupShiftTasksModal
      :visible.sync="showGroupTasksModal"
      :shift="currentShift"
      :user-id="store?.user?.id"
      @refresh="fetchCurrentShift"
      @medications-saved="onGroupMedicationSave"
    />

    <!-- Incident Modal -->
    <IncidentModal
      v-model="showIncidentModal"
      :clients="incidentClients"
      :caregivers="incidentCaregivers"
      :prefill-caregiver-id="store?.user?.id"
      :prefill-client-id="incidentPrefillClientId"
      :shift-id="incidentShiftId"
      @saved="onIncidentSaved"
    />
  </nav>
</template>
<script>
import { useLoginStore } from "src/stores/loginStore";
import ClockInModal from "@/components/ClockInModal.vue";
import ClockOutModal from "@/components/ClockOutModal.vue";
import StartBreakModal from "@/components/StartBreakModal.vue";
import GroupShiftTasksModal from "@/components/GroupShiftTasksModal.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import MedicationAdminModal from "@/components/MedicationAdminModal.vue";
import IncidentModal from "@/components/IncidentModal.vue";
import OfflineSyncStatus from "@/components/OfflineSyncStatus.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { applyPendingClockEvents } from "@/utils/pendingClockState";
import { prefetchShiftClockData } from "@/utils/clockPrefetch";
import { onChange as onQueueChange } from "@/utils/offlineQueue";
import { clientsService } from "@/serviceHandlers/clientsService";
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { isDSPDGroupShift, getCaregiverShiftStatus, areAllGroupTasksComplete } from "@/utils/shiftStatus";
import { impersonationService } from "@/serviceHandlers/impersonationService";
import { EventBus, EVENTS } from "@/utils/eventBus";

export default {
  components: {
    ClockInModal,
    ClockOutModal,
    StartBreakModal,
    GroupShiftTasksModal,
    AIVSelect,
    MedicationAdminModal,
    IncidentModal,
    OfflineSyncStatus,
  },

  data() {
    return {
      activeNotifications: false,
      store: useLoginStore(),
      currentShift: null,
      showClockInModal: false,
      showClockOutModal: false,
      showStartBreakModal: false,
      showGroupTasksModal: false,
      impersonatableUsers: [],
      selectedUserId: null,
      showMedicationModal: false,
      midShiftMedications: {},
      clientHasMedications: false,
      lastMedCheckShiftId: null,
      showIncidentModal: false,
      incidentClients: [],
      incidentCaregivers: [],
    };
  },

  computed: {
    formattedImpersonatableUsers() {
      return this.impersonatableUsers.map(user => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name} (${user.user_type})`
      }));
    },

    formattedUserType() {
      const userType = this.store?.user?.user_type || "N/A";
      return userType;
    },

    isGroupShift() {
      return this.currentShift && isDSPDGroupShift(this.currentShift);
    },

    groupShiftCaregiverStatus() {
      if (!this.isGroupShift) return null;
      return getCaregiverShiftStatus(this.currentShift, this.store?.user?.id);
    },

    // Shadow/training assignment on a non-group shift: this user's clock
    // state lives in their own side row (my_clock_status), independent of
    // the shift-level status owned by the primary caregiver
    isSideActor() {
      if (!this.currentShift) return false;
      if ((this.currentShift.shift_type || "1-on-1") === "Group") return false;
      if (this.currentShift.my_role) {
        return this.currentShift.my_role !== "primary";
      }
      // my_role only comes from the getCurrent endpoint. When the shift
      // arrived via the getByRole fallback, infer side-actor from an
      // existing side clock row (non-group primaries never have one).
      return (this.currentShift.caregiverClockTimes || []).some(
        (ct) => Number(ct.id_users) === Number(this.store?.user?.id),
      );
    },

    sideActorStatus() {
      if (!this.isSideActor) return null;
      return getCaregiverShiftStatus(this.currentShift, this.store?.user?.id);
    },

    showClockInButton() {
      if (!this.currentShift) return false;
      if (this.isGroupShift) {
        return this.groupShiftCaregiverStatus === 'scheduled';
      }
      if (this.isSideActor) {
        return this.sideActorStatus === 'scheduled';
      }
      return this.currentShift.shift_status !== 'Clocked In';
    },

    showClockOutButton() {
      if (!this.currentShift) return false;
      if (this.isGroupShift) {
        return this.groupShiftCaregiverStatus === 'clocked-in' && areAllGroupTasksComplete(this.currentShift, this.store?.user?.id);
      }
      if (this.isSideActor) {
        return this.sideActorStatus === 'clocked-in';
      }
      return this.currentShift.shift_status === 'Clocked In';
    },

    showNavTasksButton() {
      if (!this.isGroupShift) return false;
      return this.groupShiftCaregiverStatus === 'clocked-in';
    },

    showMedicationsButton() {
      if (!this.currentShift) return false;
      if (this.currentShift.payer !== 'DSPD') return false;
      if (this.currentShift.shift_status !== 'Clocked In') return false;
      if (this.isGroupShift) return false;
      return this.clientHasMedications === true;
    },

    incidentPrefillClientId() {
      if (!this.currentShift) return null;
      if (this.currentShift.shift_status !== 'Clocked In') return null;
      const clients = this.currentShift.clients || [];
      if (clients.length === 1) return clients[0].id;
      return null;
    },

    incidentShiftId() {
      if (!this.currentShift) return null;
      if (this.currentShift.shift_status !== 'Clocked In') return null;
      return this.currentShift.id;
    },

    shouldShowBreakButton() {
      if (!this.currentShift || this.currentShift.shift_status !== 'Clocked In') {
        return false;
      }

      // DSPD group shifts do not support breaks
      if (this.isGroupShift) {
        return false;
      }

      // Check if shift has JC service code
      const serviceCodes = this.currentShift.service_codes || [];
      const hasJCServiceCode = serviceCodes.some(sc => sc.code === 'JC');
      if (!hasJCServiceCode) {
        return false;
      }

      // Check shift duration >= 4 hours
      const startTime = this.currentShift.scheduled_start || this.currentShift.startTime;
      const endTime = this.currentShift.scheduled_end || this.currentShift.endTime;

      if (!startTime || !endTime) {
        return false;
      }

      // Calculate duration - handle both full ISO datetime strings and time-only strings
      let startDate, endDate;
      if (startTime.includes('T')) {
        // Full ISO datetime string
        startDate = new Date(startTime);
        endDate = new Date(endTime);
      } else {
        // Time-only string, combine with date
        const dateStr = this.currentShift.date?.split('T')[0];
        if (!dateStr) return false;
        startDate = new Date(`${dateStr}T${startTime}`);
        endDate = new Date(`${dateStr}T${endTime}`);
      }

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
      }

      const durationHours = (endDate - startDate) / (1000 * 60 * 60);
      if (durationHours < 4) {
        return false;
      }

      // Check if a break has already been completed
      // If there's any break with both break_start and break_end, don't show button
      const breaks = this.currentShift.breaks || [];
      const hasCompletedBreak = breaks.some(b => b.break_start && b.break_end);
      if (hasCompletedBreak) {
        return false;
      }

      return true;
    }
  },

  watch: {
    currentShift(newShift) {
      if (newShift) {
        this.checkClientMedications(newShift);
      } else {
        this.clientHasMedications = false;
      }
    },
    'store.isSuperAdmin': {
      immediate: true,
      handler(isSuperAdmin) {
        if (isSuperAdmin && !this.store.isImpersonating && this.impersonatableUsers.length === 0) {
          this.loadImpersonatableUsers();
        }
      },
    },
  },

  mounted() {
    this.fetchCurrentShift();
    // Refresh shift status every 30 seconds
    this.shiftRefreshInterval = setInterval(() => {
      this.fetchCurrentShift();
    }, 30000);

    // Listen for global shift saved events
    this.$root.$on('shift-saved', this.onShiftSaved);

    // Re-derive the clock button state when offline-queued events change
    // (enqueued while disconnected, or drained after reconnecting).
    this._unsubscribeQueue = onQueueChange(() => {
      this.fetchCurrentShift();
    });
  },

  beforeDestroy() {
    if (this.shiftRefreshInterval) {
      clearInterval(this.shiftRefreshInterval);
    }

    // Clean up event listener
    this.$root.$off('shift-saved', this.onShiftSaved);
    if (this._unsubscribeQueue) {
      this._unsubscribeQueue();
    }
  },

  methods: {
    async loadImpersonatableUsers() {
      try {
        const response = await impersonationService.getImpersonatableUsers();
        if (response.success) {
          this.impersonatableUsers = response.users;
        }
      } catch (error) {
        this.$notifyError("Failed to load user list for impersonation");
      }
    },

    async handleImpersonate(userId) {
      if (!userId) {
        return;
      }

      try {
        // Save current token and user as admin before impersonating
        const currentToken = localStorage.getItem("jwtToken");
        localStorage.setItem("adminToken", currentToken);
        localStorage.setItem("adminUser", JSON.stringify(this.store.user));

        const response = await impersonationService.impersonateUser(userId);

        if (response.success) {
          // Update token
          localStorage.setItem("jwtToken", response.signUpToken);

          // Update store
          this.store.setUser(response.user);
          this.store.setImpersonating(true, response.adminEmail);

          this.$notifySuccess(`Now impersonating ${response.user.first_name} ${response.user.last_name}`);

          // Navigate to dashboard
          this.$router.push({ name: 'Dashboard' });
        }
      } catch (error) {
        this.$notifyError(error.response?.data?.message || "Failed to impersonate user");
        this.selectedUserId = null;
      }
    },

    stopImpersonating() {
      // Restore the original admin token and user
      const adminToken = localStorage.getItem("adminToken");
      const adminUserStr = localStorage.getItem("adminUser");

      if (adminToken) {
        localStorage.setItem("jwtToken", adminToken);
        localStorage.removeItem("adminToken");
      }

      if (adminUserStr) {
        const adminUser = JSON.parse(adminUserStr);
        this.store.setUser(adminUser);
        localStorage.removeItem("adminUser");
      }

      // Clear impersonation state
      this.store.clearImpersonation();

      this.$notifyInfo("Stopped impersonating user");

      // Navigate to dashboard
      this.$router.push({ name: 'Dashboard' });
    },

    async fetchCurrentShift() {
      if (!this.store?.user?.id) {
        return;
      }

      try {
        // First, try to get current shift using the /current endpoint if available
        if (typeof shiftsService.getCurrent === 'function') {
          try {
            const currentResponse = await shiftsService.getCurrent({
              caregiverId: this.store.user.id,
            });

            // API returns shift data directly, not under a 'shift' property
            if (currentResponse && currentResponse.id) {
              // Overlay any offline-queued clock events so buttons/status
              // reflect what the caregiver did while disconnected.
              this.currentShift = applyPendingClockEvents(
                currentResponse,
                this.store.user.id,
              );
              // Warm the offline cache with everything needed to clock
              // in/out of this shift (throttled, fire-and-forget).
              prefetchShiftClockData(currentResponse, this.store.user.id).catch(
                () => {},
              );
              return;
            }
            // A successful null answer is authoritative: the caregiver has no
            // current shift (e.g. a shadow whose own side clock row is already
            // closed, even though the shift-level status is still the
            // primary's). Scanning getByRole here would resurrect that shift
            // from its shift-level status and offer a bogus Clock In button.
            this.currentShift = null;
            return;
          } catch (currentError) {
            // Current endpoint failed (network / older API) — fall back to getByRole
          }
        }

        // Fallback to the original method
        const today = new Date();
        const startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

        const response = await shiftsService.getByRole({
          startDate: startDate,
          endDate: startDate,
          caregiverId: this.store.user.id,
          userType: this.store.user.user_type || "Caregiver",
        });

        const shifts = response?.shifts || [];

        const activeShift = shifts.find((shift) => {
          const isAssignedToShift =
            shift.caregivers &&
            shift.caregivers.some(
              (caregiver) =>
                Number(caregiver.id) === Number(this.store.user.id),
            );

          if (!isAssignedToShift) {
            return false;
          }

          // Per-caregiver status: side actors (shadow/training) and group
          // members are judged by their own clock row, not the shift-level
          // status — a completed side row must never resurface as clockable.
          const status = getCaregiverShiftStatus(shift, this.store.user.id);

          // Priority 1: If already clocked in, always show regardless of time window
          if (status === "clocked-in") {
            return true;
          }

          // Priority 2: Check if it's a scheduled shift for today that we can clock into
          if (status !== "scheduled") {
            return false;
          }

          const today = new Date();
          const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
          const shiftDateStr = shift.date.split("T")[0];
          const isToday = todayStr === shiftDateStr;

          if (!isToday) {
            return false;
          }

          const now = new Date();
          const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

          const startTime = shift.scheduled_start || shift.startTime || "";
          const endTime = shift.scheduled_end || shift.endTime || "";

          if (startTime && endTime) {
            const convertUTCTimeToLocalMinutes = (timeStr, dateStr) => {
              // Handle full UTC datetime strings (e.g. "2026-04-03T14:00:00.000Z")
              if (timeStr && timeStr.includes("T")) {
                const utcDate = new Date(timeStr);
                if (!isNaN(utcDate.getTime())) {
                  return utcDate.getHours() * 60 + utcDate.getMinutes();
                }
              }

              // Legacy format: time-only string (e.g. "14:00:00")
              const utcDateTime = `${dateStr}T${timeStr}Z`;
              const utcDate = new Date(utcDateTime);
              if (!isNaN(utcDate.getTime())) {
                return utcDate.getHours() * 60 + utcDate.getMinutes();
              }
              const [hour, min] = timeStr.split(":").map(Number);
              return hour * 60 + min;
            };

            const startMinutes = convertUTCTimeToLocalMinutes(
              startTime,
              shiftDateStr,
            );
            const endMinutes = convertUTCTimeToLocalMinutes(
              endTime,
              shiftDateStr,
            );

            // CMP exception: allow clock-in any time during the calendar day
            const isCMPShift = shift.service_codes &&
              shift.service_codes.some(sc => sc.code === 'CMP');

            const allowedStartMinutes = startMinutes - 30;
            const isInTimeWindow = isCMPShift
              ? true
              : (currentTimeMinutes >= allowedStartMinutes &&
                 currentTimeMinutes < endMinutes);

            return isInTimeWindow;
          }

          return false;
        });

        this.currentShift = activeShift
          ? applyPendingClockEvents(activeShift, this.store.user.id)
          : null;
        if (activeShift) {
          prefetchShiftClockData(activeShift, this.store.user.id).catch(
            () => {},
          );
        }
      } catch (error) {
        // Error fetching current shift
      }
    },

    async handleClockAction() {
      if (!this.currentShift) return;

      // Determine whether this is a clock-in or clock-out before nudging.
      let clockingOut;
      if (this.isGroupShift) {
        clockingOut = this.groupShiftCaregiverStatus === "clocked-in";
      } else if (this.isSideActor) {
        clockingOut = this.sideActorStatus === "clocked-in";
      } else {
        clockingOut = this.currentShift.shift_status === "Clocked In";
      }

      // Nudge toward the mobile app before opening the web clock flow.
      if (!(await this.$clockNudge(clockingOut ? "out" : "in"))) return;

      if (clockingOut) {
        this.showClockOutModal = true;
      } else {
        this.showClockInModal = true;
      }
    },

    openGroupTasks() {
      this.showGroupTasksModal = true;
    },

    onClockInSuccess(response) {
      this.fetchCurrentShift();
      this.$emit("shift-updated");
      EventBus.$emit(EVENTS.SHIFT_CLOCK_IN);
    },

    async checkClientMedications(shift) {
      if (!shift || shift.payer !== 'DSPD') {
        this.clientHasMedications = false;
        return;
      }
      // Cache by shift ID to avoid re-fetching every 30s refresh
      if (this.lastMedCheckShiftId === shift.id) return;
      this.lastMedCheckShiftId = shift.id;

      const shiftClients = shift.clients || shift?.originalData?.clients || [];
      if (shiftClients.length === 0) {
        this.clientHasMedications = false;
        return;
      }
      const clientId = shiftClients[0].id;
      if (!clientId) {
        this.clientHasMedications = false;
        return;
      }
      try {
        const response = await clientsService.getMedications(clientId);
        const meds = (response.medications || []).filter(m => m.active !== false);
        this.clientHasMedications = meds.length > 0;
      } catch {
        this.clientHasMedications = false;
      }
    },

    onMedicationSave(data) {
      this.midShiftMedications = data;
    },

    onGroupMedicationSave(medicationAdminData) {
      this.midShiftMedications = { ...this.midShiftMedications, ...medicationAdminData };
    },

    onClockOutSuccess(response) {
      this.midShiftMedications = {};
      // Immediately clear the current shift to hide the button
      this.currentShift = null;
      // Add a small delay to ensure the API has updated the shift status
      setTimeout(() => {
        this.fetchCurrentShift();
      }, 500);
      this.$emit("shift-updated");
      EventBus.$emit(EVENTS.SHIFT_CLOCK_OUT);
    },

    handleStartBreak() {
      if (!this.currentShift) return;
      this.showStartBreakModal = true;
    },

    onBreakActionSuccess(response) {
      // Instantly update the isOnBreak flag based on the response
      if (this.currentShift && response && response.break) {
        // If breakEnd is null, the break was started; otherwise it was ended
        this.currentShift.isOnBreak = response.break.breakEnd === null;
      }

      // Refresh the current shift data after break action
      this.fetchCurrentShift();
      this.$emit("shift-updated");
    },

    async openIncidentModal() {
      this.showIncidentModal = true;
      try {
        const [clientsResponse, caregiversResponse] = await Promise.all([
          clientsService.list({ slim: true }),
          caregiversService.list(new URLSearchParams({ includeAdmins: "true" })),
        ]);
        this.incidentClients = (clientsResponse || []).map(c => ({
          id: Number(c.id) || c.id,
          first_name: c.first_name,
          last_name: c.last_name,
        }));
        this.incidentCaregivers = (caregiversResponse || []).map(c => ({
          id: Number(c.id) || c.id,
          first_name: c.first_name,
          last_name: c.last_name,
        }));
      } catch (error) {
        // Dropdowns will just be empty if loading fails
      }
    },

    onIncidentSaved() {
      this.showIncidentModal = false;
    },

    onShiftSaved() {
      // Refresh current shift when a shift is saved from any page
      this.fetchCurrentShift();
    },

    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },

    goToMyProfile() {
      const userId = this.store.user.id;
      this.$router.push(`/pages/caregivers/${userId}`);
    },

    logout() {
      localStorage.removeItem("jwtToken");
      this.store.$reset();
      this.$router.push({
        name: "Login",
      });
    },
  },
};
</script>
<style scoped>
.nc-icon {
  color: white;
}
.navbar {
  border-bottom: 1px solid rgba(205, 144, 189, 0.3);
}
.nav-link.d-flex i {
  margin-right: 0.5rem;
  color: white;
}
.profile-link {
  border: 1px solid white;
  padding: 6px 12px !important;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.profile-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 0.8;
}

.profile-link,
.profile-link i,
.profile-link .profile-text {
  color: white !important;
}

.nav-link.d-flex.align-items-center.p-0:not(.profile-link) {
  border: 1px solid #17a2b8;
  padding: 6px 12px !important;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.nav-link.d-flex.align-items-center.p-0:not(.profile-link):hover {
  background-color: rgba(23, 162, 184, 0.1);
}

.navbar-actions .nav-link:not(.profile-link) i {
  color: #17a2b8;
}

.navbar-actions .logout-text {
  color: #17a2b8;
  font-size: 0.9rem;
}

.profile-text {
  font-size: 0.9rem;
}

.user-info {
  font-size: 0.9rem;
}
.user-name {
  font-weight: 500;
}
.user-type {
  font-size: 0.75rem;
}

/* Hamburger menu styles */
.navbar-toggler {
  background: transparent;
  border: none;
  padding: 4px 8px;
  margin-right: 15px;
  cursor: pointer;
  align-self: center;
  display: flex;
  align-items: center;
}

.navbar-toggler:focus {
  outline: none;
}

.navbar-toggler-icon {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 20px;
  height: 15px;
}

.icon-bar {
  display: block;
  width: 100%;
  height: 2px;
  background-color: white;
  border-radius: 1px;
  transition: all 0.3s ease;
}

/* Ensure consistent vertical alignment */
.container-fluid {
  display: flex;
  align-items: center;
  min-height: 60px;
}

.navbar-brand {
  display: flex;
  align-items: center;
}

.navbar-actions {
  display: flex;
  align-items: center;
}

/* Responsive adjustments for mobile */
@media (max-width: 991px) {
  .container-fluid {
    justify-content: space-between;
    align-items: center;
  }

  .navbar-brand {
    flex: 1;
    justify-content: flex-start;
    align-items: center;
  }

  .navbar-actions {
    margin-left: auto;
    align-items: center;
  }

  .user-info {
    font-size: 0.8rem;
  }

  .user-type {
    font-size: 0.7rem;
  }

  .clock-btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }

  .break-btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }

  .meds-btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }

  .incident-btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }

  .logout-text {
    font-size: 0.8rem;
  }

  .profile-text {
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .user-info .user-type {
    display: none;
  }

  .user-info {
    font-size: 0.75rem;
  }

  .user-name {
    font-size: 0.75rem;
  }

  .clock-btn {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
    margin-left: 0.5rem !important;
  }

  .clock-btn .mr-1 {
    margin-right: 0.2rem !important;
  }

  .break-btn {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
    margin-left: 0.5rem !important;
  }

  .break-btn .mr-1 {
    margin-right: 0.2rem !important;
  }

  .meds-btn {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
    margin-left: 0.5rem !important;
  }

  .meds-btn .mr-1 {
    margin-right: 0.2rem !important;
  }

  .incident-btn {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
    margin-left: 0.5rem !important;
  }

  .incident-btn .mr-1 {
    margin-right: 0.2rem !important;
  }

  .logout-text {
    font-size: 0.75rem;
  }

  .profile-text {
    font-size: 0.75rem;
  }

  .navbar-actions .nav-link {
    padding: 0.25rem !important;
  }

  .navbar-actions .nc-icon {
    font-size: 14px;
  }

  /* Adjust spacing for very small screens */
  .navbar-brand {
    margin-right: 0.5rem;
  }
}

@media (max-width: 400px) {
  .user-name {
    font-size: 0.7rem;
  }

  .logout-text {
    font-size: 0.7rem;
  }

  .profile-text {
    font-size: 0.7rem;
  }

  .navbar-actions .nav-link {
    padding: 0.25rem !important;
  }

  .clock-btn {
    font-size: 0.65rem;
    padding: 0.15rem 0.3rem;
  }

  .break-btn {
    font-size: 0.65rem;
    padding: 0.15rem 0.3rem;
  }

  .meds-btn {
    font-size: 0.65rem;
    padding: 0.15rem 0.3rem;
  }

  .incident-btn {
    font-size: 0.65rem;
    padding: 0.15rem 0.3rem;
  }
}
.clock-btn {
  font-size: 0.85rem;
  padding: 0.375rem 0.75rem;
}

/* Clock In button - matches "Clocked In" status color (bright yellow) */
.clock-in-btn {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 0.7)) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  color: #000 !important;
}
.clock-in-btn:hover {
  background: linear-gradient(135deg, rgba(255, 193, 7, 1), rgba(255, 193, 7, 0.8)) !important;
  border: 1px solid rgba(0, 0, 0, 0.3) !important;
  color: #000 !important;
}

/* Clock Out button - matches "Clocked Out" status color (muted orange) */
.clock-out-btn {
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.7), rgba(237, 137, 54, 0.5)) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.clock-out-btn:hover {
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.8), rgba(237, 137, 54, 0.6)) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

/* Medications button - Green gradient */
.meds-btn {
  font-size: 0.85rem;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.8), rgba(40, 167, 69, 0.6)) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.meds-btn:hover {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.9), rgba(40, 167, 69, 0.7)) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

/* Break button - Teal/Cyan color to distinguish from Clock Out */
.break-btn {
  font-size: 0.85rem;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.8), rgba(23, 162, 184, 0.6)) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.break-btn:hover {
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.9), rgba(23, 162, 184, 0.7)) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

/* Incident button - warning/orange style */
.incident-btn {
  font-size: 0.85rem;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.8), rgba(220, 53, 69, 0.6)) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.incident-btn:hover {
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.9), rgba(220, 53, 69, 0.7)) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

/* Impersonation Select Dropdown */
.impersonation-select {
  /* Uses AIVSelect default styling */
}

.impersonation-select >>> .aiv-select-input::placeholder {
  color: #b3a6a6 !important;
}

/* Responsive for impersonation elements */
@media (max-width: 991px) {
  .impersonation-select {
    width: 180px !important;
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .impersonation-select {
    width: 150px !important;
    font-size: 0.75rem;
  }
}
</style>
